import { TrendDown01, TrendUp01 } from "@untitledui/icons";
import type { OpenCollaborationInsightsSummary, TargetCollaborationInsightsSummary } from "@/api/collaborations";
import { Skeleton } from "@/components/ui/skeleton";

type Props = {
  data: TargetCollaborationInsightsSummary | OpenCollaborationInsightsSummary | null | undefined;
  isLoading: boolean;
  /** When provided, the Total GMV tile becomes clickable and applies the "Generated GMV" status filter on the creators table below. */
  onTotalGmvClick?: () => void;
};

function trendPercent(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

function TrendBadge({ current, previous }: { current: number; previous: number }) {
  const pct = trendPercent(current, previous);
  if (pct === null) return null;

  const positive = pct >= 0;
  return (
    <div className={`flex items-center gap-1 text-xs font-medium ${positive ? "text-[#4ADE80]" : "text-[#F87171]"}`}>
      {positive ? <TrendUp01 className="w-3.5 h-3.5 shrink-0" /> : <TrendDown01 className="w-3.5 h-3.5 shrink-0" />}
      <span>{Math.abs(pct)}% vs last month</span>
    </div>
  );
}

function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

type KpiCardProps = {
  label: string;
  value: string;
  current: number;
  previous: number;
  onClick?: () => void;
};

const cardStyle: React.CSSProperties = {
  borderRadius: "var(--radius-md, 8px)",
  border: "1px solid var(--Component-colors-Alpha-alpha-white-20, rgba(255, 255, 255, 0.20))",
  background:
    "var(--Background, linear-gradient(180deg, rgba(249, 245, 240, 0.13) 0%, rgba(147, 145, 142, 0.13) 100%))",
  backdropFilter: "blur(2px)",
};

function KpiCard({ label, value, current, previous, onClick }: KpiCardProps) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      className={`flex flex-col p-4 transition-colors text-left ${
        onClick ? "cursor-pointer hover:border-white/30 outline-none" : ""
      }`}
      style={{ ...cardStyle, width: 160, height: 125, gap: 6 }}
      onClick={onClick}
    >
      <span className="text-2xl font-medium text-white leading-tight">{value}</span>
      <span className="text-xs text-white/60 font-medium">{label}</span>
      <TrendBadge current={current} previous={previous} />
    </Tag>
  );
}

export function InsightsSummaryCards({ data, isLoading, onTotalGmvClick }: Props) {
  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <Skeleton className="h-5 w-48 bg-white/20 mb-8" />
        <div className="grid" style={{ gridTemplateColumns: "160px 160px", gap: 20 }}>
          {(["gmv", "creators", "rate", "videos"] as const).map((key) => (
            <div key={key} className="flex flex-col p-4" style={{ ...cardStyle, width: 160, height: 125, gap: 6 }}>
              <Skeleton className="h-7 w-24 bg-white/20" />
              <Skeleton className="h-3 w-20 bg-white/20" />
              <Skeleton className="h-3 w-28 bg-white/10" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="text-sm text-white/50 text-center py-4">No summary data available</div>;
  }

  const { totalCreators, kpis } = data;
  const currency = kpis.totalGmv.currency;

  return (
    <div className="flex flex-col h-full">
      <p className="text-base font-semibold text-white mb-8">
        {totalCreators.toLocaleString()} creators in Collaboration
      </p>
      <div className="grid" style={{ gridTemplateColumns: "160px 160px", gap: 20 }}>
        <KpiCard
          label="Total GMV"
          value={formatCurrency(kpis.totalGmv.current, currency)}
          current={kpis.totalGmv.current}
          previous={kpis.totalGmv.previous}
          onClick={onTotalGmvClick}
        />
        <KpiCard
          label="Active Creators"
          value={kpis.activeCreators.current.toLocaleString()}
          current={kpis.activeCreators.current}
          previous={kpis.activeCreators.previous}
        />
        <KpiCard
          label="Posting Rate"
          value={formatPercent(kpis.postingRate.current)}
          current={kpis.postingRate.current}
          previous={kpis.postingRate.previous}
        />
        <KpiCard
          label="Videos Posted"
          value={kpis.videosPosted.current.toLocaleString()}
          current={kpis.videosPosted.current}
          previous={kpis.videosPosted.previous}
        />
      </div>
    </div>
  );
}
